import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Reservations.css';
import './ServiceDocuments.css';
import Fallback from '../../components/common/Fallback';
import DocumentUploadInput from '../../components/common/DocumentUploadInput';
import {
  getServiceById,
  getServiceDocuments,
  getServiceDocumentUrl,
  downloadServiceDocument,
  deleteServiceDocument,
  updateService,
  uploadServiceDocument
} from '../../services/serviceService';
import {
  createDepositForService,
  deleteDeposit,
  getDepositsByService,
  updateDeposit
} from '../../services/depositService';
import DepositModal from '../../components/modals/DepositModal';
import ServiceDetailsModal from '../../components/modals/ServiceDetailsModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Modal from '../../components/common/Modal';
import { getOperators } from '../../services/operatorService';
import Table from '../../components/common/Table';
import { formatDate } from '../../utils/date';
import '../../components/common/IconButton.css';
import { IconPencil, IconTrash } from '../../components/common/Icons';

const formatMoney = (currency, value) =>
  `${currency || '$'} ${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

const formatDepositType = (type) => {
  const t = String(type || '').trim().toLowerCase();
  if (!t) return '-';
  if (t === 'tranferencia' || t === 'transferencia') return 'Transferencia';
  if (t === 'efectivo') return 'Efectivo';
  if (t === 'pago directo del pasajero') return 'Pago directo del pasajero';
  return type;
};

const ServicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState({});
  const [pendingDeleteDocument, setPendingDeleteDocument] = useState(null);
  const [openServiceDocumentMenu, setOpenServiceDocumentMenu] = useState(null);
  const serviceDocumentMenuRef = useRef(null);
  const serviceDocumentMenuButtonRef = useRef(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [operators, setOperators] = useState([]);

  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [depositDeleteConfirmOpen, setDepositDeleteConfirmOpen] = useState(false);
  const [pendingDeleteDeposit, setPendingDeleteDeposit] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [profitabilityDateModalOpen, setProfitabilityDateModalOpen] = useState(false);
  const [profitabilityDateDraft, setProfitabilityDateDraft] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const [svc, deps, serviceDocuments] = await Promise.all([
        getServiceById(id),
        getDepositsByService(id),
        getServiceDocuments(id).catch((error) => {
          console.error('No se pudieron cargar los documentos del servicio:', error);
          return [];
        })
      ]);
      setService(svc);
      setDeposits(deps || []);
      setDocuments(serviceDocuments || []);
    } catch (err) {
      console.error(err);
      if (err?.message?.toLowerCase().includes('not found')) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!openServiceDocumentMenu) return undefined;

    const closeMenuOnOutsideClick = (event) => {
      if (!serviceDocumentMenuRef.current?.contains(event.target)) {
        setOpenServiceDocumentMenu(null);
      }
    };
    const closeMenuOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenServiceDocumentMenu(null);
        serviceDocumentMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', closeMenuOnOutsideClick);
    document.addEventListener('keydown', closeMenuOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeMenuOnOutsideClick);
      document.removeEventListener('keydown', closeMenuOnEscape);
    };
  }, [openServiceDocumentMenu]);

  useEffect(() => {
    getOperators().then(setOperators).catch((err) => console.error(err));
  }, []);

  const hasCost = useMemo(() => {
    const raw = service?.cost;
    if (raw === null || raw === undefined || raw === '') return false;
    const num = Number(raw);
    return !Number.isNaN(num) && num !== 0;
  }, [service?.cost]);

  const bankExpenseValue = useMemo(() => {
    const raw = service?.bank_expense ?? service?.bankExpense;
    if (raw === null || raw === undefined || raw === '') return null;
    const num = Number(raw);
    return Number.isNaN(num) ? null : num;
  }, [service?.bank_expense, service?.bankExpense]);

  const conversionValue = useMemo(() => {
    const raw = service?.conversion;
    if (raw === null || raw === undefined || raw === '') return null;
    const num = Number(raw);
    return Number.isNaN(num) ? null : num;
  }, [service?.conversion]);

  const profitability = useMemo(() => {
    if (!hasCost) return null;
    const price = Number(service?.price || 0);
    const cost = Number(service?.cost || 0);
    const bankExpense = Number(bankExpenseValue || 0);
    return price - cost - bankExpense;
  }, [service, hasCost, bankExpenseValue]);

  const saldoAbonado = useMemo(() => {
    return deposits.reduce((acc, d) => acc + Number(d.value || 0), 0);
  }, [deposits]);

  const saldoRestante = useMemo(() => {
    return Number(service?.cost || 0) - saldoAbonado;
  }, [service?.cost, saldoAbonado]);

  const dateProfitability = service?.date_profitability ?? service?.dateProfitability ?? null;
  const dateProfitabilityInputValue = dateProfitability ? String(dateProfitability).slice(0, 10) : '';

  const buildServicePayload = (data) => {
    const bill =
      data?.bill === undefined
        ? (service?.bill ?? null)
        : (data.bill === '' || data.bill === null ? null : data.bill);

    return {
      operatorId: Number(data.operatorId ?? service?.operator_id ?? service?.operatorId),
      type: data.type ?? service?.type ?? '',
      description: data.description ?? service?.description ?? null,
      dateOut: data.dateOut ?? service?.date_out ?? service?.dateOut ?? null,
      dateIn: data.dateIn ?? service?.date_in ?? service?.dateIn ?? null,
      currency: data.currency ?? service?.currency ?? '$',
      conversion:
        (data.currency ?? service?.currency ?? '$') === '$'
          ? (
            data.conversion === '' || data.conversion === null || data.conversion === undefined
              ? (
                data.conversion === ''
                  ? null
                  : (service?.conversion ?? null)
              )
              : Number(data.conversion)
          )
          : null,
      price: Number(data.price ?? service?.price ?? 0),
      // El estado no se edita manualmente desde UI. Se preserva el valor actual.
      status: service?.status || 'Incompleto',
      file: data.file ?? service?.file ?? null,
      cost:
        data.cost === '' || data.cost === null || data.cost === undefined
          ? (data.cost === '' ? null : (service?.cost ?? null))
          : Number(data.cost),
      bankExpense:
        data.bankExpense === '' || data.bankExpense === null || data.bankExpense === undefined
          ? (
            data.bankExpense === ''
              ? null
              : (service?.bank_expense ?? service?.bankExpense ?? null)
          )
          : Number(data.bankExpense),
      bill,
      dateProfitability: data.dateProfitability ?? dateProfitabilityInputValue
    };
  };

  const handleSaveDeposit = async (data) => {
    const payload = {
      date: data.date,
      value: Number(data.value || 0),
      currency: service?.currency || '$',
      type: data.type,
      receipt: data.receipt || null
    };

    if (selectedDeposit) {
      await updateDeposit(selectedDeposit.id, payload);
    } else {
      await createDepositForService(id, payload);
    }
    setDepositModalOpen(false);
    setSelectedDeposit(null);
    await loadData();
  };

  const handleDeleteDeposit = async (depositId) => {
    await deleteDeposit(depositId);
    setDepositModalOpen(false);
    setSelectedDeposit(null);
    await loadData();
  };

  const handleSaveService = async (data, modalType) => {
    const payload = buildServicePayload(data);

    await updateService(service.id, payload);
    if (modalType === 'details') setDetailsModalOpen(false);
    if (modalType === 'summary') setSummaryModalOpen(false);
    await loadData();
  };

  const handleUploadServiceDocument = async (type, file) => {
    setUploadingDocuments((previous) => ({ ...previous, [type]: true }));
    try {
      const uploadedDocument = await uploadServiceDocument(service.id, type, file);
      setDocuments((previous) => [
        ...previous.filter((document) => document.document_type !== type),
        uploadedDocument
      ]);
    } catch (error) {
      console.error('No se pudo subir el documento del servicio:', error);
    } finally {
      setUploadingDocuments((previous) => ({ ...previous, [type]: false }));
    }
  };

  const handleDeleteServiceDocument = async () => {
    if (!pendingDeleteDocument) return;
    await deleteServiceDocument(service.id, pendingDeleteDocument.type);
    setDocuments((previous) => previous.filter(
      (document) => document.document_type !== pendingDeleteDocument.type
    ));
    setPendingDeleteDocument(null);
  };

  const openProfitabilityDateModal = () => {
    setProfitabilityDateDraft(dateProfitabilityInputValue);
    setProfitabilityDateModalOpen(true);
  };

  const handleSaveProfitabilityDate = async () => {
    const payload = buildServicePayload({
      dateProfitability: profitabilityDateDraft || null
    });

    await updateService(service.id, payload);
    setProfitabilityDateModalOpen(false);
    await loadData();
  };

  if (loading || !service) {
    return (
      <div className="page-wrapper">
        {loading && <p>Cargando servicio...</p>}
        {!loading && notFound && (
          <Fallback
            title="Servicio no encontrado"
            description="El servicio solicitado no existe."
          />
        )}
        {!loading && !notFound && <Fallback title="Error" description="Error al cargar el servicio." />}
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div className="actions-row" style={{ gap: 12 }}>
          <h1 style={{ margin: 0 }}>Servicio</h1>
          <button className="btn secondary" onClick={() => navigate(`/backoffice/ficha/${service.file_id}`)}>
            Ver ficha #{service.file_id}
          </button>
        </div>

        <div className="file-status-card">
          <div className="file-status-label">Estado</div>
          <div
            className={`file-status-value file-status-value--regular ${
              service.status === 'Completo'
                ? 'completo'
                : service.status === 'Deprecado'
                  ? 'deprecado'
                  : 'incompleto'
            }`}
          >
            {service.status || 'Incompleto'}
          </div>
          {service.status === 'Completo' ? (
            <div className="file-status-sub">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>Fecha de rentabilidad: {formatDate(dateProfitability)}</span>
                <button
                  type="button"
                  className="icon-btn icon-btn--select"
                  onClick={openProfitabilityDateModal}
                  aria-label="Editar fecha de rentabilidad"
                  title="Editar fecha de rentabilidad"
                >
                  <IconPencil />
                </button>
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="section-card">
        <div className="actions-row" style={{ gap: 8, alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Detalles</h3>
          <button
            type="button"
            className="icon-btn icon-btn--select"
            onClick={() => setDetailsModalOpen(true)}
            aria-label="Editar detalles del servicio"
            title="Editar detalles"
          >
            <IconPencil />
          </button>
        </div>
        <div className="grid-summary" style={{ marginTop: 10 }}>
          <div className="summary-item" style={{ gridColumn: '1 / -1' }}>
            <div><b>Descripción</b></div>
            <div>{service.description || '-'}</div>
          </div>
        </div>

        <div className="grid-summary" style={{ marginTop: 8 }}>
          <div className="summary-item">
            <div><b>Operador</b></div>
            <div>{service.operator_name || '-'}</div>
          </div>
          <div className="summary-item">
            <div><b>Tipo</b></div>
            <div>{service.type || '-'}</div>
          </div>
          <div className="summary-item">
            <div><b>Fecha de salida</b></div>
            <div>{formatDate(service.date_in)}</div>
          </div>
          <div className="summary-item">
            <div><b>Fecha de vuelta</b></div>
            <div>{formatDate(service.date_out)}</div>
          </div>
        </div>
      </div>

      <div className="section-card">
        <h3 className="service-documents-title" style={{ marginTop: 0 }}>Documentación</h3>
        <div className="service-document-list">
          {[
            { type: 'reservation', label: 'Reserva' },
            { type: 'invoice', label: 'Factura' },
            { type: 'receipt', label: 'Recibo' },
            { type: 'liquidation', label: 'Liquidación' }
          ].map(({ type, label }) => {
            const document = documents.find((item) => item.document_type === type);
            const isUploading = Boolean(uploadingDocuments[type]);
            const documentUrl = document ? getServiceDocumentUrl(service.id, type) : null;
            const documentActions = document ? (
              <div
                className="service-document-menu"
                ref={openServiceDocumentMenu === type ? serviceDocumentMenuRef : null}
              >
                <button
                  ref={openServiceDocumentMenu === type ? serviceDocumentMenuButtonRef : null}
                  type="button"
                  className="service-document-menu-trigger"
                  aria-label={`Más acciones para ${label.toLowerCase()}`}
                  aria-expanded={openServiceDocumentMenu === type}
                  title="Más acciones"
                  onClick={() => setOpenServiceDocumentMenu((current) => current === type ? null : type)}
                >
                  <span className="service-document-menu-dots" aria-hidden="true">⋮</span>
                </button>
                {openServiceDocumentMenu === type && (
                  <div className="service-document-menu-popover" id={`service-document-menu-${type}`}>
                    <a
                      className="service-document-menu-action"
                      href={documentUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setOpenServiceDocumentMenu(null)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M14 4h6v6M20 4l-9 9" />
                        <path d="M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />
                      </svg>
                      <span>Ver</span>
                    </a>
                    <button
                      type="button"
                      className="service-document-menu-action"
                      onClick={() => {
                        setOpenServiceDocumentMenu(null);
                        downloadServiceDocument(service.id, type, document.original_name);
                      }}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
                        <path d="M4 17v3h16v-3" />
                      </svg>
                      <span>Descargar</span>
                    </button>
                    <button
                      type="button"
                      className="service-document-menu-action service-document-menu-action--delete"
                      onClick={() => {
                        setOpenServiceDocumentMenu(null);
                        setPendingDeleteDocument({ type, label, name: document.original_name });
                      }}
                    >
                      <IconTrash />
                      <span>Eliminar</span>
                    </button>
                  </div>
                )}
              </div>
            ) : null;

            return (
              <DocumentUploadInput
                key={type}
                title={label}
                description={document?.original_name || 'Sin archivo cargado'}
                hasFile={Boolean(document)}
                showUploadButton={!document}
                inputLabel={`Subir ${label.toLowerCase()}`}
                busy={isUploading}
                actions={documentActions}
                onFileSelected={(file) => handleUploadServiceDocument(type, file)}
              />
            );
          })}
        </div>
      </div>

      <div className="section-card">
        <div className="actions-row" style={{ gap: 8, alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Resumen</h3>
          <button
            type="button"
            className="icon-btn icon-btn--select"
            onClick={() => setSummaryModalOpen(true)}
            aria-label="Editar resumen del servicio"
            title="Editar resumen"
          >
            <IconPencil />
          </button>
        </div>

        <div className="grid-summary" style={{ marginTop: 10 }}>
          <div className="summary-item" style={{ gridColumn: '1 / -1' }}>
            <div><b>File / Liquidación</b></div>
            <div>{service.file || '-'}</div>
          </div>
          <div className="summary-item">
            <div><b>Costo</b></div>
            <div>{hasCost ? formatMoney(service.currency, service.cost) : '-'}</div>
          </div>
          <div className="summary-item">
            <div style={{ color: service.currency === '$' ? undefined : '#9ca3af' }}><b>Gasto bancario</b></div>
            <div style={{ color: service.currency === '$' ? undefined : '#9ca3af' }}>{bankExpenseValue === null ? '-' : formatMoney(service.currency, bankExpenseValue)}</div>
          </div>
          <div className="summary-item">
            <div><b>Precio</b></div>
            <div>{formatMoney(service.currency, service.price)}</div>
          </div>
          <div className="summary-item">
            <div style={{ color: service.currency === '$' ? undefined : '#9ca3af' }}><b>Conversión</b></div>
            <div
              style={{
                color: service.currency === '$' ? undefined : '#9ca3af'
              }}
            >
              {conversionValue === null ? '-' : Number(conversionValue).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
            </div>
          </div>
          <div className="summary-item">
            <div><b>Rentabilidad</b></div>
            <div>{hasCost ? formatMoney(service.currency, profitability) : '-'}</div>
          </div>
        </div>
      </div>

      <div className="section-card">
        <div className="flex-between">
          <h3 style={{ margin: 0 }}>Órdenes de pago</h3>
          <div className="actions-row">
            {!hasCost ? (
              <button className="btn" onClick={() => setSummaryModalOpen(true)}>
                Agregar costo
              </button>
            ) : (
              <button
                className="btn"
                onClick={() => { setSelectedDeposit(null); setDepositModalOpen(true); }}
                disabled={service?.status === 'Completo'}
              >
                + Agregar orden de pago
              </button>
            )}
          </div>
        </div>

        {!hasCost ? (
          <div className="empty-state">
            Debes agregar el costo del servicio antes de cargar una orden de pago.
          </div>
        ) : (!deposits || deposits.length === 0) ? (
          <p className="empty-state">No hay órdenes de pago cargadas aún.</p>
        ) : (
          <Table
            data={deposits}
            rowKey="id"
            className="table service-payment-table"
            emptyMessage="No hay órdenes de pago cargadas aún."
            columns={[
              {
                id: 'date',
                header: 'Fecha',
                accessor: (d) => formatDate(d.date),
                sortAccessor: (d) => d.date || ''
              },
              { id: 'type', header: 'Tipo', accessor: (d) => formatDepositType(d.type) },
              {
                id: 'money',
                header: 'Monto',
                accessor: (d) => formatMoney(d.currency || '$', d.value),
                sortComparator: (a, b) => {
                  const ca = a?.currency ?? '';
                  const cb = b?.currency ?? '';
                  const cRes = String(ca).localeCompare(String(cb), undefined, { sensitivity: 'base' });
                  if (cRes !== 0) return cRes;
                  return Number(a?.value || 0) - Number(b?.value || 0);
                },
                sortAccessor: (d) => `${d.currency || ''}|${Number(d.value || 0)}`
              },
              { id: 'receipt', header: 'Recibo', accessor: (d) => d.receipt || '-' },
              {
                id: 'actions',
                header: 'Acciones',
                headerClassName: 'th-center',
                sortable: false,
                filterable: false,
                cell: (d) => (
                  <div className="inline-actions">
                    <button
                      type="button"
                      className="icon-btn icon-btn--edit"
                      onClick={() => { setSelectedDeposit(d); setDepositModalOpen(true); }}
                      aria-label="Editar orden de pago"
                      title="Editar"
                    >
                      <IconPencil />
                    </button>
                    <button
                      type="button"
                      className="icon-btn icon-btn--delete"
                      onClick={() => { setPendingDeleteDeposit(d); setDepositDeleteConfirmOpen(true); }}
                      aria-label="Eliminar orden de pago"
                      title="Eliminar"
                    >
                      <IconTrash />
                    </button>
                  </div>
                )
              }
            ]}
          />
        )}

        {hasCost && (
          <div className="grid-summary" style={{ marginTop: 16 }}>
            <div className="summary-item">
              <div><b>Saldo abonado</b></div>
              <div>{formatMoney(service.currency, saldoAbonado)}</div>
            </div>
            <div className="summary-item">
              <div><b>Saldo restante</b></div>
              <div style={{
                fontWeight: 600,
                color: saldoRestante > 0 ? '#dc2626' : saldoRestante < 0 ? '#d97706' : '#16a34a'
              }}>
                {formatMoney(service.currency, saldoRestante)}
              </div>
            </div>
          </div>
        )}
      </div>

      <DepositModal
        isOpen={depositModalOpen}
        onClose={() => setDepositModalOpen(false)}
        onSave={handleSaveDeposit}
        onDelete={selectedDeposit ? () => handleDeleteDeposit(selectedDeposit.id) : undefined}
        initialData={selectedDeposit}
        serviceCurrency={service?.currency || '$'}
      />

      <ServiceDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        onSave={(data) => handleSaveService(data, 'details')}
        initialData={service}
        operators={operators}
        mode="details"
      />

      <ServiceDetailsModal
        isOpen={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        onSave={(data) => handleSaveService(data, 'summary')}
        initialData={service}
        operators={operators}
        mode="summary"
      />

      <Modal
        title="Editar fecha de rentabilidad"
        isOpen={profitabilityDateModalOpen}
        onClose={() => setProfitabilityDateModalOpen(false)}
        contentClassName="modal-content-confirm"
        footer={(
          <div className="modal-footer">
            <div className="footer-actions">
              <button type="button" className="btn secondary" onClick={() => setProfitabilityDateModalOpen(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn"
                style={{ backgroundColor: '#1d4ed8', color: '#fff' }}
                onClick={handleSaveProfitabilityDate}
              >
                Guardar
              </button>
            </div>
          </div>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label htmlFor="profitability-date-input" style={{ fontWeight: 600 }}>
            Fecha de rentabilidad
          </label>
          <input
            id="profitability-date-input"
            type="date"
            className="search-input"
            value={profitabilityDateDraft}
            onChange={(e) => setProfitabilityDateDraft(e.target.value)}
          />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={depositDeleteConfirmOpen}
        title="Eliminar orden de pago"
        message="¿Está seguro que desea eliminar esta orden de pago?"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        onConfirm={async () => {
          if (!pendingDeleteDeposit?.id) return;
          await handleDeleteDeposit(pendingDeleteDeposit.id);
          setDepositDeleteConfirmOpen(false);
          setPendingDeleteDeposit(null);
        }}
        onCancel={() => {
          setDepositDeleteConfirmOpen(false);
          setPendingDeleteDeposit(null);
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteDocument)}
        title={`Eliminar ${pendingDeleteDocument?.label?.toLowerCase() || 'documento'}`}
        message={`¿Está seguro que desea eliminar ${pendingDeleteDocument?.name || 'este documento'}?`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        onConfirm={handleDeleteServiceDocument}
        onCancel={() => setPendingDeleteDocument(null)}
      />

    </div>
  );
};

export default ServicePage;
